import { forwardRef, memo } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/styles';
import { Loader } from '../Loader';

const darkModeSharedStyles = [
  'dark:text-white dark:fill-white',
  'dark:bg-white/20 dark:hover:bg-white/30 dark:active:bg-white/40',
];

const buttonVariants = cva(
  'flex items-center gap-0.5 rounded-sm truncate transition-all data-[disabled=true]:cursor-not-allowed relative',
  {
    variants: {
      variant: {
        primary: [
          'text-white fill-white',
          'bg-bg-inter-primary-default hover:bg-bg-inter-primary-hover active:bg-bg-inter-primary-pressed',
          'border border-bg-inter-primary-default hover:border-bg-inter-primary-hover active:border-bg-inter-primary-pressed',
          'data-[disabled=true]:bg-transparent data-[disabled=true]:border-border-default data-[disabled=true]:text-text-disabled data-[disabled=true]:fill-icon-disabled',
          ...darkModeSharedStyles,
        ].join(' '),
        secondary: [
          'text-text-primary fill-icon-primary',
          'bg-bg-inter-secondary-default hover:bg-bg-inter-secondary-hover active:bg-bg-inter-secondary-pressed',
          'border border-bg-inter-secondary-default hover:border-bg-inter-secondary-hover active:border-bg-inter-secondary-pressed',
          'data-[disabled=true]:bg-transparent data-[disabled=true]:border-border-default data-[disabled=true]:text-text-disabled data-[disabled=true]:fill-icon-disabled',
          ...darkModeSharedStyles,
          'dark:border-[#3F3F3F] dark:shadow-xsm',
        ].join(' '),
        tertiary: [
          'text-text-primary fill-icon-primary',
          'bg-bg-inter-tertiary-default hover:bg-bg-inter-tertiary-hover active:bg-bg-inter-tertiary-pressed',
          'data-[disabled=true]:bg-transparent data-[disabled=true]:text-text-disabled data-[disabled=true]:fill-icon-disabled',
        ].join(' '),
        outline: [
          'text-text-primary fill-icon-primary',
          'bg-bg-inter-tertiary-default hover:bg-bg-inter-tertiary-hover active:bg-bg-inter-tertiary-pressed',
          'border border-border-default',
          'data-[disabled=true]:bg-transparent data-[disabled=true]:border-border-default data-[disabled=true]:text-text-disabled data-[disabled=true]:fill-icon-disabled',
          ...darkModeSharedStyles,
          'dark:border-[#3F3F3F] dark:shadow-xsm',
        ].join(' '),
      },
      size: {
        lg: 'h-10 px-4 font-body-md font-semibold',
        md: 'h-8 px-3 font-body-sm font-semibold',
        sm: 'h-7 px-2.5 font-body-sm font-semibold rounded-[40px]',
      },
      color: {
        default: '',
        alert: [
          'text-text-primary fill-text-primary',
          'bg-orange-3 hover:bg-orange-4 active:bg-orange-4',
          'border border-orange-3 hover:border-orange-4 active:border-orange-4',
        ].join(' '),
        destructive: [
          'text-text-destructive fill-text-destructive',
          'bg-bg-inter-destructive-hover hover:bg-bg-inter-destructive-pressed active:bg-bg-inter-destructive-pressed',
          'border border-bg-inter-destructive-hover hover:border-bg-inter-destructive-pressed active:border-bg-inter-destructive-pressed',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      color: 'default',
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  asChild?: boolean;
  loading?: boolean;
  active?: boolean;
  element?: React.ElementType;
}

export type ButtonSize = VariantProps<typeof buttonVariants>['size'];

const ButtonBase = (
  {
    children,
    className,
    variant,
    size,
    color,
    disabled = false,
    leftIcon,
    rightIcon,
    asChild = false,
    loading = false,
    active = false,
    element = 'button',
    type = 'button',
    ...rest
  }: ButtonProps,
  ref: React.Ref<HTMLButtonElement>,
) => {
  const Comp: React.ElementType = asChild ? Slot : element;

  const isMd = size === 'md' || !size;

  return (
    <Comp
      ref={ref}
      className={cn(
        buttonVariants({ variant, size, color }),
        { 'cursor-pointer': !disabled && !loading, 'pointer-events-none': loading || disabled },
        { 'pl-2': !!leftIcon && isMd, 'pr-2': !!rightIcon && isMd },
        {
          'bg-bg-inter-secondary-active': active && variant === 'tertiary',
          'bg-bg-inter-secondary-pressed': active && variant === 'secondary',
          'bg-bg-inter-secondary-active!': active && variant === 'outline',
        },
        className,
      )}
      disabled={disabled}
      data-disabled={disabled}
      type={type}
      {...rest}
    >
      {loading && <Loader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" inverted />}

      <div
        data-slot="content"
        className={cn('flex items-center justify-center gap-0.5 w-full', { 'invisible pointer-events-none': loading })}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </div>
    </Comp>
  );
};

export const Button = memo(forwardRef(ButtonBase));

export { buttonVariants };
